import React, { useState } from 'react';
import { Card } from '../../../../components/Card/Card';
import { Order, refreshOrderStatus, updateOrder, isClearingOrder, deleteOrder } from '../../../../model/order';

import { Icon } from '../../../../components/Icon/Icon';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { PaymentModal } from '../modals/PaymentModal';
import { OrdersList } from '../OrdersList/OrdersList';
import { t } from '../../../../locales/translate';
import { PaymentModel } from '../../../../model/payment';
import { handleError, handleSuccess } from '../../../../model/notification';
import { useConfirmation, useSmartConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { TimeRange } from '../../../../model/util';
import { ClearingOrderModal } from './ClearingOrderModal';
import { PermissionButton } from '../../../../components/Button/PermissionButton';
import { history } from '../../../../model/history';
import { ordersPath } from '../../../../routes';
import { presence } from '../../../../util/util';
import { useMutationHandler } from '../../../../model/graphql';
import { graphql } from '../../../../generated';

interface Props {
  order: Order;
  onReloadActivities: () => void;
  onRefresh: () => void;
}

const RESEND_CONFIRMATION_EMAIL = graphql(`
  mutation ResendConfirmationEmail($orderId: ID!) {
    resendConfirmationEmail(input: { orderId: $orderId }) {
      order {
        id
      }
    }
  }
`);

const RESEND_ONLINE_TICKETS = graphql(`
  mutation ResendOnlineTickets($orderId: ID!) {
    resendOnlineTickets(input: { orderId: $orderId }) {
      order {
        id
      }
    }
  }
`);

export function GeneralInfo({ order, onReloadActivities, onRefresh }: Props): JSX.Element {
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayClearingModal, setDisplayClearingModal] = useState(false);
  const confirm = useSmartConfirmation();
  const [resendConfirmationEmail] = useMutationHandler(RESEND_CONFIRMATION_EMAIL);
  const [resendOnlineTickets] = useMutationHandler(RESEND_ONLINE_TICKETS);

  async function handleOnRefresh(): Promise<void> {
    setLoading(true);
    try {
      await refreshOrderStatus(order.id);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  const { confirm: confirmProblem } = useConfirmation({
    title: order.problem ? t.model.order.unsetProblem : t.model.order.setProblem,
    message: order.problem ? t.model.order.unsetProblemMsg : t.model.order.setProblemMsg,
    action: async () => {
      await updateOrder({
        orderId: order.id,
        problem: !order.problem,
      });
    },
  });

  const { confirm: confirmDelete } = useConfirmation({
    title: t.model.order.deleteOrderTitle,
    message: t.model.order.deleteOrderConfirmation,
    action: async () => {
      await deleteOrder({ orderId: order.id });
      history.push(ordersPath());
      handleSuccess(t.model.order.orderDeleted);
    },
  });

  const userName =
    (order.user &&
      order.preferredBillingAddress &&
      (presence(
        [order.preferredBillingAddress.firstName, order.preferredBillingAddress.lastName]
          .filter(Boolean)
          .join(' ')
          .trim()
      ) ||
        [order.user.firstName, order.user.lastName].filter(Boolean).join(' '))) ??
    null;

  return (
    <>
      <Card
        title={[
          order.reference && order.reference.trim(),
          order.user ? userName : order.company ? order.company.name : '',
        ]
          .filter(Boolean)
          .join(' - ')}
        actions={
          <>
            {isClearingOrder(order) && order.$permissions.includes('create_clearing') && (
              <Icon name="calendar" onClick={() => setDisplayClearingModal(true)}></Icon>
            )}
            <Icon className={order.problem ? 'active' : ''} name="problem" onClick={confirmProblem} />
            <PermissionButton
              name="speedemail"
              hideLock
              model={order}
              permission="resend_online_tickets"
              tooltip={t.model.order.resendOnlineTickets}
              onClick={() =>
                confirm({
                  title: t.model.order.resendOnlineTickets,
                  action: () => resendOnlineTickets({ variables: { orderId: order.id } }),
                })
              }
            />
            <PermissionButton
              name="speedemail"
              hideLock
              model={order}
              permission="resend_confirmation_email"
              tooltip={t.model.order.resendConfirmationMail}
              onClick={() =>
                confirm({
                  title: t.model.order.resendConfirmationMail,
                  action: () => resendConfirmationEmail({ variables: { orderId: order.id } }),
                })
              }
            />
            <Icon name="euro-sign" onClick={() => setShowAddPaymentModal(true)} />
            <Icon name="refresh" loading={loading} onClick={() => handleOnRefresh()} />
            <PermissionButton
              model={order}
              permission="destroy"
              name="trash"
              onClick={confirmDelete}
              hideLock
              tooltip={t.model.order.deleteOrderTitle}
            />
          </>
        }
        elevation={2}
        fullbleed
      >
        <OrdersList
          orders={[order]}
          addPaymentColumn={false}
          displayRowActions
          showBalance
          displayDeleteDocuments
          onDocumentChange={() => {
            onReloadActivities();
            onRefresh();
          }}
          hideUser
        />
      </Card>
      <ControlledModal<PaymentModel>
        open={showAddPaymentModal}
        onClose={() => {
          setShowAddPaymentModal(false);
        }}
        title={t.actions.add(t.model.payment._name)}
        size="small"
        model={{
          provider: order.preferredPaymentMethod,
          amount: order.balance,
          status: 'paid',
        }}
      >
        {(controller) => <PaymentModal controller={controller} orderId={order.id} mode="create" />}
      </ControlledModal>
      <ControlledModal<TimeRange>
        open={displayClearingModal}
        onClose={() => {
          setDisplayClearingModal(false);
          onRefresh();
        }}
        title={t.model.order.startClearingCycle}
        size="small"
        saveTitleOverride={t.general.start}
      >
        {(controller) => <ClearingOrderModal orderId={order.id} controller={controller} />}
      </ControlledModal>
    </>
  );
}
